import React, { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { c } from 'vite/dist/node/types.d-aGj9QkWt';

import { useUpdateRestaurantMutation } from '@/api/hooks/useRestaurant';
import { RestaurantResponse } from '@/api/lib/restaurant';

import deliverooLogo from '../../../assets/images/deliveroo.png';
import justEatlogo from '../../../assets/images/just-eat.png';
import customDeliveryLogo from '../../../assets/images/other-delivery.png';
import uberEatlogo from '../../../assets/images/uber-eat.png';
import { RadioGroup } from '@/components/forms/base/RadioGroup';
import { TextInput } from '@/components/forms/base/TextInput';
import { Form } from '@/components/forms/core/Form';
import { AutoCompleteTagHandle, AutoCompleteTagInput } from '@/components/forms/custom/AutocompleteTagInput';
import { Button } from '@/components/molecules/Button';

import { UpdateRestaurantSchemaType, validationSchema } from './schema';

export interface RestaurantFormProps {
  restaurant?: RestaurantResponse;
}

export const RestaurantForm = ({ restaurant }: RestaurantFormProps) => {
  const { t } = useTranslation();
  const autoCompleteHandle = useRef<AutoCompleteTagHandle>(null);

  const { isLoading, mutateAsync: updateRestaurant } = useUpdateRestaurantMutation({
    onSuccess: () => {
      toast.success(t('action.restaurantUpdated'), {
        position: 'bottom-right'
      });
    }
  });

  const handleSubmit = async (data: UpdateRestaurantSchemaType) => {
    if (!restaurant?.id) {
      toast.error(t('proposeRestaurant.pleaseSelectRestaurant'), {
        position: 'bottom-right'
      });
      return;
    }
    if (!data.restaurantId) {
      data.restaurantId = restaurant?.id;
    }

    await updateRestaurant(data);
  };

  useEffect(() => {
    if (restaurant) {
      const tagIds = restaurant.tags.map((tag) => ({ id: tag.id, text: tag.name }));
      autoCompleteHandle.current?.setSuggestions(tagIds);
    } else {
      autoCompleteHandle.current?.resetSuggestions();
    }
  }, [restaurant]);

  return (
    <div className="gap-4 flex flex-col w-full">
      <Form
        data-testid="restaurant-form"
        schema={validationSchema(t)}
        onSubmit={handleSubmit}
        className="flex flex-col gap-5"
        defaultValues={{
          uberEatsLink: restaurant?.delivery?.uberEats,
          justEatLink: restaurant?.delivery?.justEat,
          deliverooLink: restaurant?.delivery?.deliveroo,
          customDeliveryLink: restaurant?.delivery?.custom
        }}
        onlyTouchedFields={false}
        render={({ formState }) => {
          return (
            <>
              <div className="grid md:grid-cols-2 gap-2 md:gap-x-12">
                <TextInput id="uberEatsLink" logo={uberEatlogo} label={t('form.uberEatsLink')} placeholder={t('proposeRestaurant.deliveryLink')} />
                <TextInput id="deliverooLink" logo={deliverooLogo} label={t('form.deliverooLink')} placeholder={t('proposeRestaurant.deliveryLink')} />
                <TextInput id="justEatLink" logo={justEatlogo} label={t('form.justEatLink')} placeholder={t('proposeRestaurant.deliveryLink')} />
                <TextInput
                  id="customDeliveryLink"
                  logo={customDeliveryLogo}
                  label={t('form.customDeliveryLink')}
                  placeholder={t('proposeRestaurant.deliveryLink')}
                />
              </div>
              <RadioGroup
                id="alcohol"
                name="alcohol"
                label={t('form.alcoholLabel')}
                horizontal
                defaultValue={restaurant?.alcohol ? 'true' : 'false'}
                required
                options={[
                  { value: 'false', title: t('action.no') },
                  { value: 'true', title: t('action.yes') }
                ]}
              />
              <AutoCompleteTagInput
                ref={autoCompleteHandle}
                searchTagInputId="autocompleteTag"
                searchTagInputIds="tagIds"
                label={t('form.specialties')}
                weight="bold"
                placeholder={t('form.restaurantSpecialties')}
                required
              />
              <div>
                <Button fullWidth type="submit" color="primary" label={t('form.save')} loading={isLoading} disabled={!formState.isDirty} />
              </div>
            </>
          );
        }}
      />
    </div>
  );
};
